// Centralized "start a conversation" flow. Wraps messaging-api's
// createConversation so every callsite gets identical gate handling:
//   RESTRICTED_BY_PREFS → prime the gate cache + route to the gated screen
//   BLOCKED             → toast, no navigation
//   anything else       → toast the server message
// Returns the conversation on success, null when handled (gated/blocked/error).

import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AgentMode, ConversationSummary } from '@tabbio/types';
import { createConversation, type GateAwareError } from '@/lib/messaging-api';
import { QUERY_KEYS } from '@/lib/query-keys';
import { useToast } from '@/hooks/useToast';

export type StartConversationFailureReason = 'gated' | 'blocked' | 'error';

export function useStartConversation() {
  const router = useRouter();
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation('common');

  return useCallback(
    async (
      targetUserId: string,
      opts?: {
        mode?: AgentMode;
        companyId?: string;
        jobId?: string;
        // Runs right before navigating to the gated screen: lets modal /
        // compose-mode callsites close their own chrome first.
        beforeGateNavigate?: () => void;
        // Optional outcome hook for root-level handoffs that must distinguish
        // a routed preference gate from a terminal API failure.
        onHandledFailure?: (reason: StartConversationFailureReason) => void;
      },
    ): Promise<ConversationSummary | null> => {
      const { beforeGateNavigate, onHandledFailure, ...createOpts } = opts ?? {};
      try {
        return await createConversation(targetUserId, createOpts);
      } catch (error) {
        const gateError = error as GateAwareError;
        if (gateError.code === 'RESTRICTED_BY_PREFS' && gateError.gate) {
          qc.setQueryData(
            QUERY_KEYS.MESSAGING_GATED_RECIPIENT(targetUserId),
            gateError.gate,
          );
          beforeGateNavigate?.();
          router.push(`/messages/gated/${targetUserId}` as never);
          onHandledFailure?.('gated');
          return null;
        }
        if (gateError.code === 'BLOCKED') {
          toast.error(t('messages.blockedError'));
          onHandledFailure?.('blocked');
          return null;
        }
        toast.error(gateError.message ?? t('messages.conversationFailed'));
        onHandledFailure?.('error');
        return null;
      }
    },
    [qc, router, toast, t],
  );
}
